import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '../../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  CreditCard, Wallet, Users, Settings, Plus, Check, X, ExternalLink,
  RefreshCw, Tag, Percent, DollarSign, Building, Link2, Unlink, Zap,
  Crown, Star, Sparkles, Gift
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminPayments() {
  const [activeTab, setActiveTab] = useState('transactions');
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Memberships
  const [memberships, setMemberships] = useState([]);
  const [showMembershipDialog, setShowMembershipDialog] = useState(false);
  const [membershipForm, setMembershipForm] = useState({
    name: '',
    description: '',
    price: 0,
    interval: 'monthly',
    features: [],
    is_active: true
  });
  
  // Coupons
  const [coupons, setCoupons] = useState([]);
  const [showCouponDialog, setShowCouponDialog] = useState(false);
  const [couponForm, setCouponForm] = useState({
    code: '',
    discount_type: 'percentage',
    discount_value: 10,
    max_uses: 0,
    expires_at: ''
  });
  
  // Connect accounts
  const [connectedAccounts, setConnectedAccounts] = useState([]);
  
  // Stripe config
  const [stripeConfig, setStripeConfig] = useState({
    publishable_key: '',
    secret_key: '',
    webhook_secret: '',
    connect_enabled: false
  });

  useEffect(() => {
    fetchPayments();
    fetchMemberships();
    fetchCoupons();
    fetchConnectedAccounts();
    fetchStripeConfig();
  }, [statusFilter]);

  const getAuthHeaders = () => ({
    'Authorization': `Bearer ${localStorage.getItem('admin_token')}`,
    'Content-Type': 'application/json'
  });

  const fetchPayments = async () => {
    try {
      let url = `${API_URL}/api/admin/payments`;
      if (statusFilter !== 'all') url += `?status=${statusFilter}`;
      
      const response = await fetch(url, { headers: getAuthHeaders() });
      if (response.ok) {
        const data = await response.json();
        setPayments(data);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMemberships = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/memberships`, { headers: getAuthHeaders() });
      if (response.ok) {
        const data = await response.json();
        setMemberships(data);
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchCoupons = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/coupons`, { headers: getAuthHeaders() });
      if (response.ok) {
        const data = await response.json();
        setCoupons(data);
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchConnectedAccounts = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/stripe/connected-accounts`, { headers: getAuthHeaders() });
      if (response.ok) {
        const data = await response.json();
        setConnectedAccounts(data);
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchStripeConfig = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/stripe/config`, { headers: getAuthHeaders() });
      if (response.ok) {
        const data = await response.json();
        setStripeConfig(data);
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const saveMembership = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/memberships`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(membershipForm)
      });
      if (response.ok) {
        toast.success('Membresía creada');
        setShowMembershipDialog(false);
        fetchMemberships();
      }
    } catch (error) {
      toast.error('Error al crear membresía');
    }
  };

  const saveCoupon = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/coupons`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(couponForm)
      });
      if (response.ok) {
        toast.success('Cupón creado');
        setShowCouponDialog(false);
        fetchCoupons();
      }
    } catch (error) {
      toast.error('Error al crear cupón');
    }
  };

  const saveStripeConfig = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/stripe/config`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(stripeConfig)
      });
      if (response.ok) {
        toast.success('Configuración de Stripe guardada');
      }
    } catch (error) {
      toast.error('Error al guardar configuración');
    }
  };

  const statusColors = {
    pending: 'bg-amber-500/10 text-amber-600 border-amber-500/30',
    paid: 'bg-green-500/10 text-green-600 border-green-500/30',
    refunded: 'bg-blue-500/10 text-blue-600 border-blue-500/30',
    failed: 'bg-red-500/10 text-red-600 border-red-500/30',
    held: 'bg-purple-500/10 text-purple-600 border-purple-500/30',
  };

  const totalPaid = payments.filter(p => p.status === 'paid').reduce((sum, p) => sum + p.amount, 0);

  return (
    <div className="min-h-screen" data-testid="admin-payments-page">
      <header className="sticky top-0 z-30 h-14 glass flex items-center justify-between px-6">
        <h1 className="text-lg font-semibold">Pagos y Suscripciones</h1>
        <Badge variant="outline" className="bg-green-500/10 text-green-600">
          ${totalPaid.toLocaleString()} MXN
        </Badge>
      </header>

      <div className="p-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="transactions" className="flex items-center gap-2">
              <CreditCard className="h-4 w-4" /> Transacciones
            </TabsTrigger>
            <TabsTrigger value="memberships" className="flex items-center gap-2">
              <Crown className="h-4 w-4" /> Membresías
            </TabsTrigger>
            <TabsTrigger value="coupons" className="flex items-center gap-2">
              <Tag className="h-4 w-4" /> Cupones
            </TabsTrigger>
            <TabsTrigger value="connect" className="flex items-center gap-2">
              <Link2 className="h-4 w-4" /> Stripe Connect
            </TabsTrigger>
            <TabsTrigger value="config" className="flex items-center gap-2">
              <Settings className="h-4 w-4" /> Configuración
            </TabsTrigger>
          </TabsList>

          {/* Transactions Tab */}
          <TabsContent value="transactions">
            <div className="flex items-center justify-between mb-6">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Estado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="pending">Pendiente</SelectItem>
                  <SelectItem value="paid">Pagado</SelectItem>
                  <SelectItem value="refunded">Reembolsado</SelectItem>
                  <SelectItem value="failed">Fallido</SelectItem>
                </SelectContent>
              </Select>
              <Badge variant="outline">{payments.length} transacciones</Badge>
            </div>

            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>ID Sesión</TableHead>
                    <TableHead>Restaurante</TableHead>
                    <TableHead className="text-right">Monto</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead>Fecha</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {payments.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                        No hay transacciones
                      </TableCell>
                    </TableRow>
                  ) : (
                    payments.map((payment) => (
                      <TableRow key={payment.id}>
                        <TableCell>
                          <code className="text-xs bg-muted p-1 rounded">{payment.session_id?.slice(0, 12)}...</code>
                        </TableCell>
                        <TableCell>
                          <code className="text-xs">{payment.restaurant_id?.slice(0, 8)}...</code>
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          ${payment.amount?.toLocaleString()} {payment.currency}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs">{payment.payment_type}</Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={statusColors[payment.status]}>{payment.status}</Badge>
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {new Date(payment.created_at).toLocaleDateString()}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>

          {/* Memberships Tab */}
          <TabsContent value="memberships">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold">Planes de Membresía</h2>
              <Button onClick={() => setShowMembershipDialog(true)}>
                <Plus className="h-4 w-4 mr-2" /> Nueva Membresía
              </Button>
            </div>

            <div className="grid gap-4 md:grid-cols-3">
              {memberships.length === 0 ? (
                <>
                  {/* Default membership cards */}
                  <Card className="border-2 border-dashed">
                    <CardHeader>
                      <div className="flex items-center gap-2">
                        <Star className="h-5 w-5 text-slate-400" />
                        <CardTitle>Básico</CardTitle>
                      </div>
                      <CardDescription>Para pequeños restaurantes</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <p className="text-3xl font-bold">$499<span className="text-sm font-normal text-muted-foreground">/mes</span></p>
                      <ul className="mt-4 space-y-2 text-sm">
                        <li className="flex items-center gap-2"><Check className="h-4 w-4 text-green-500" /> Hasta 50 reservaciones/mes</li>
                        <li className="flex items-center gap-2"><Check className="h-4 w-4 text-green-500" /> Widget básico</li>
                        <li className="flex items-center gap-2"><Check className="h-4 w-4 text-green-500" /> Soporte por email</li>
                      </ul>
                    </CardContent>
                  </Card>

                  <Card className="border-2 border-orange-500/50 bg-orange-500/5">
                    <CardHeader>
                      <div className="flex items-center gap-2">
                        <Crown className="h-5 w-5 text-orange-500" />
                        <CardTitle>Pro</CardTitle>
                        <Badge className="bg-orange-500">Popular</Badge>
                      </div>
                      <CardDescription>Para restaurantes en crecimiento</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <p className="text-3xl font-bold">$999<span className="text-sm font-normal text-muted-foreground">/mes</span></p>
                      <ul className="mt-4 space-y-2 text-sm">
                        <li className="flex items-center gap-2"><Check className="h-4 w-4 text-green-500" /> Reservaciones ilimitadas</li>
                        <li className="flex items-center gap-2"><Check className="h-4 w-4 text-green-500" /> Eventos y experiencias</li>
                        <li className="flex items-center gap-2"><Check className="h-4 w-4 text-green-500" /> Integraciones</li>
                        <li className="flex items-center gap-2"><Check className="h-4 w-4 text-green-500" /> Soporte prioritario</li>
                      </ul>
                    </CardContent>
                  </Card>

                  <Card className="border-2 border-purple-500/50 bg-purple-500/5">
                    <CardHeader>
                      <div className="flex items-center gap-2">
                        <Sparkles className="h-5 w-5 text-purple-500" />
                        <CardTitle>Enterprise</CardTitle>
                      </div>
                      <CardDescription>Para cadenas de restaurantes</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <p className="text-3xl font-bold">$2,499<span className="text-sm font-normal text-muted-foreground">/mes</span></p>
                      <ul className="mt-4 space-y-2 text-sm">
                        <li className="flex items-center gap-2"><Check className="h-4 w-4 text-green-500" /> Todo de Pro</li>
                        <li className="flex items-center gap-2"><Check className="h-4 w-4 text-green-500" /> Multi-ubicación</li>
                        <li className="flex items-center gap-2"><Check className="h-4 w-4 text-green-500" /> API personalizada</li>
                        <li className="flex items-center gap-2"><Check className="h-4 w-4 text-green-500" /> Gerente de cuenta</li>
                      </ul>
                    </CardContent>
                  </Card>
                </>
              ) : (
                memberships.map((membership) => (
                  <Card key={membership.id}>
                    <CardHeader>
                      <CardTitle>{membership.name}</CardTitle>
                      <CardDescription>{membership.description}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <p className="text-3xl font-bold">
                        ${membership.price}
                        <span className="text-sm font-normal text-muted-foreground">/{membership.interval}</span>
                      </p>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          {/* Coupons Tab */}
          <TabsContent value="coupons">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold">Cupones de Descuento</h2>
              <Button onClick={() => setShowCouponDialog(true)}>
                <Plus className="h-4 w-4 mr-2" /> Nuevo Cupón
              </Button>
            </div>

            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Código</TableHead>
                    <TableHead>Descuento</TableHead>
                    <TableHead>Usos</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead>Expira</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {coupons.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                        <Gift className="h-8 w-8 mx-auto mb-2 opacity-50" />
                        No hay cupones. Crea el primero.
                      </TableCell>
                    </TableRow>
                  ) : (
                    coupons.map((coupon) => (
                      <TableRow key={coupon.id}>
                        <TableCell>
                          <code className="font-bold">{coupon.code}</code>
                        </TableCell>
                        <TableCell>
                          {coupon.discount_type === 'percentage' ? `${coupon.discount_value}%` : `$${coupon.discount_value}`}
                        </TableCell>
                        <TableCell>{coupon.uses_count || 0}/{coupon.max_uses || '∞'}</TableCell>
                        <TableCell>
                          <Badge className={coupon.is_active ? 'bg-green-500/10 text-green-600' : 'bg-red-500/10 text-red-600'}>
                            {coupon.is_active ? 'Activo' : 'Inactivo'}
                          </Badge>
                        </TableCell>
                        <TableCell>{coupon.expires_at || 'Sin expiración'}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>

          {/* Stripe Connect Tab */}
          <TabsContent value="connect">
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Link2 className="h-5 w-5" /> Stripe Connect
                  </CardTitle>
                  <CardDescription>
                    Permite que los restaurantes reciban pagos directamente a través de tu cuenta de Stripe.
                    Tú actúas como intermediario y puedes cobrar comisiones.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg mb-4">
                    <div>
                      <p className="font-medium">Modo Connect</p>
                      <p className="text-sm text-muted-foreground">
                        Los restaurantes crean su cuenta de Stripe vinculada a tu plataforma
                      </p>
                    </div>
                    <Switch
                      checked={stripeConfig.connect_enabled}
                      onCheckedChange={(checked) => setStripeConfig({ ...stripeConfig, connect_enabled: checked })}
                    />
                  </div>

                  <h3 className="font-medium mb-3">Cuentas Conectadas ({connectedAccounts.length})</h3>
                  
                  {connectedAccounts.length === 0 ? (
                    <div className="text-center py-8 border-2 border-dashed rounded-lg">
                      <Building className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                      <p className="text-muted-foreground">No hay restaurantes conectados</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        Los restaurantes pueden conectar su cuenta desde su dashboard
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {connectedAccounts.map((account) => (
                        <div key={account.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                          <div>
                            <p className="font-medium">{account.restaurant_name}</p>
                            <code className="text-xs text-muted-foreground">{account.stripe_account_id}</code>
                          </div>
                          <Badge className={account.charges_enabled ? 'bg-green-500/10 text-green-600' : 'bg-yellow-500/10 text-yellow-600'}>
                            {account.charges_enabled ? 'Activo' : 'Pendiente'}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Config Tab */}
          <TabsContent value="config">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="h-5 w-5" /> Configuración de Stripe
                </CardTitle>
                <CardDescription>
                  Configura tus credenciales de Stripe para procesar pagos
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Publishable Key</Label>
                  <Input
                    value={stripeConfig.publishable_key}
                    onChange={(e) => setStripeConfig({ ...stripeConfig, publishable_key: e.target.value })}
                    placeholder="pk_live_..."
                  />
                </div>
                <div>
                  <Label>Secret Key</Label>
                  <Input
                    type="password"
                    value={stripeConfig.secret_key}
                    onChange={(e) => setStripeConfig({ ...stripeConfig, secret_key: e.target.value })}
                    placeholder="sk_live_..."
                  />
                </div>
                <div>
                  <Label>Webhook Secret</Label>
                  <Input
                    type="password"
                    value={stripeConfig.webhook_secret}
                    onChange={(e) => setStripeConfig({ ...stripeConfig, webhook_secret: e.target.value })}
                    placeholder="whsec_..."
                  />
                </div>
                <Button onClick={saveStripeConfig}>
                  Guardar Configuración
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Membership Dialog */}
      <Dialog open={showMembershipDialog} onOpenChange={setShowMembershipDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nueva Membresía</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nombre</Label>
              <Input
                value={membershipForm.name}
                onChange={(e) => setMembershipForm({ ...membershipForm, name: e.target.value })}
                placeholder="Ej: Plan Pro"
              />
            </div>
            <div>
              <Label>Descripción</Label>
              <Textarea
                value={membershipForm.description}
                onChange={(e) => setMembershipForm({ ...membershipForm, description: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Precio</Label>
                <Input
                  type="number"
                  value={membershipForm.price}
                  onChange={(e) => setMembershipForm({ ...membershipForm, price: parseFloat(e.target.value) })}
                />
              </div>
              <div>
                <Label>Intervalo</Label>
                <Select
                  value={membershipForm.interval}
                  onValueChange={(v) => setMembershipForm({ ...membershipForm, interval: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">Mensual</SelectItem>
                    <SelectItem value="yearly">Anual</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMembershipDialog(false)}>Cancelar</Button>
            <Button onClick={saveMembership}>Crear Membresía</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Coupon Dialog */}
      <Dialog open={showCouponDialog} onOpenChange={setShowCouponDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nuevo Cupón</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Código</Label>
              <Input
                value={couponForm.code}
                onChange={(e) => setCouponForm({ ...couponForm, code: e.target.value.toUpperCase() })}
                placeholder="Ej: DESCUENTO20"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Tipo de descuento</Label>
                <Select
                  value={couponForm.discount_type}
                  onValueChange={(v) => setCouponForm({ ...couponForm, discount_type: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Porcentaje</SelectItem>
                    <SelectItem value="fixed">Monto fijo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Valor</Label>
                <Input
                  type="number"
                  value={couponForm.discount_value}
                  onChange={(e) => setCouponForm({ ...couponForm, discount_value: parseFloat(e.target.value) })}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Máximo de usos (0 = ilimitado)</Label>
                <Input
                  type="number"
                  value={couponForm.max_uses}
                  onChange={(e) => setCouponForm({ ...couponForm, max_uses: parseInt(e.target.value) })}
                />
              </div>
              <div>
                <Label>Fecha de expiración</Label>
                <Input
                  type="date"
                  value={couponForm.expires_at}
                  onChange={(e) => setCouponForm({ ...couponForm, expires_at: e.target.value })}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCouponDialog(false)}>Cancelar</Button>
            <Button onClick={saveCoupon}>Crear Cupón</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
